// Container component is a central part of Flux architecture.
// In order to familiarize yourself with Flux please visit https://facebook.github.io/flux/
// This file is almost copy from Alt's implementation of Flux's Container idea. http://alt.js.org/docs/components/altContainer/
import React from 'react'
import PropTypes from 'prop-types';

const id = it => it
const getStateFromStore = (store, props) => {
  if(typeof store === 'function') {
    return store(props).value;
  } else if(Array.isArray(store)) {
    const transform = store[1];
    if(typeof transform !== 'function') throw new Error('transform property of a store tuple has to be a function');
    return transform(store[0]);
  } else {
    return store.getState()
  }
}
const getStateFromKey = (actions, props) => {
  return typeof actions === 'function' ? actions(props) : actions
}

const getStateFromActions = (props) => {
  if (props.actions) {
    return getStateFromKey(props.actions, props)
  } else {
    return {}
  }
}

const getInjected = (props) => {
  if (props.inject) {
    return Object.keys(props.inject).reduce((obj, key) => {
      obj[key] = getStateFromKey(props.inject[key], props)
      return obj
    }, {})
  } else {
    return {}
  }
}

const reduceState = (props) => {
  return Object.assign(
    {},
    getStateFromStores(props),
    getStateFromActions(props),
    getInjected(props)
  )
}

const getStateFromStores = (props) => {
  var stores = props.stores
  if (props.store) {
    return getStateFromStore(props.store, props)
  } else if (props.stores) {
    // If you pass in an array of stores then we are just listening to them
    // it should be an object then the state is added to the key specified
    if (!Array.isArray(stores)) {
      return Object.keys(stores).reduce(function (obj, key) {
        obj[key] = getStateFromStore(stores[key], props)
        return obj
      }, {})
    } else if(props.merge) {
      return Object.assign({}, ...stores.map(store => getStateFromStore(store, props)));
    }

  } else {
    return {}
  }
}

// TODO need to copy some other contextTypes maybe?
// what about propTypes?
export class Container extends React.Component<any, any> {
  static contextTypes = {
    flux: PropTypes.object,
  }

  static childContextTypes = {
    flux: PropTypes.object,
  }

  private storeListeners = [];

  getChildContext() {
    var flux = this.props.flux || this.context.flux
    return flux ? { flux: flux } : {}
  }

  constructor(props) {
    super(props)

    if (props.stores && props.store) {
      throw new ReferenceError('Cannot define both store and stores')
    }

    this.storeListeners = []

    this.state = reduceState(props)
  }

  componentWillReceiveProps(nextProps) {
    this._destroySubscriptions()
    this.setState(reduceState(nextProps))
    this._registerStores(nextProps)
    if (this.props.onWillReceiveProps) {
      this.props.onWillReceiveProps(nextProps, this.props, this.context)
    }
  }

  componentDidMount() {
    this._registerStores(this.props)
    if (this.props.onMount) this.props.onMount(this.props, this.context)
  }

  componentWillUnmount() {
    this._destroySubscriptions()
    if (this.props.onWillUnmount) {
      this.props.onWillUnmount(this.props, this.context)
    }
  }

  _registerStores(props) {
    const stores = props.stores

    if (props.store) {
      this._addSubscription(props.store)
    } else if (props.stores) {
      if (Array.isArray(stores)) {
        stores.forEach(store => {
          if(Array.isArray(store)) {
            if(store.length !== 2) throw new Error('store tuple has to have length 2');
            this._addSubscription(store[0]);
          } else {
            this._addSubscription(store);
          }
        })
      } else {
        Object.keys(stores).forEach((formatter) => {
          this._addSubscription(stores[formatter])
        })
      }
    }

    if(props.subscribe) {
      if(!Array.isArray(props.subscribe)) throw new Error('subscribe property has to be an array');
      props.subscribe.forEach(store => this._addSubscription(store));
    }
  }

  _destroySubscriptions() {
    this.storeListeners.forEach(storeListener => storeListener.remove())
  }

  _addSubscription(getStore) {
    const store = typeof getStore === 'function'
      ? getStore(this.props).store
      : getStore

    this.storeListeners.push(store.addListener('change', this.altSetState))
  }

  altSetState = () => {
    this.setState(reduceState(this.props))
  }

  getProps() {
    var flux = this.props.flux || this.context.flux
    var transform = typeof this.props.transform === 'function'
      ? this.props.transform
      : id
    return transform(Object.assign(
      flux ? { flux: flux } : {},
      this.state
    ))
  }

  shouldComponentUpdate(nextProps, nextState) {
    return this.props.shouldComponentUpdate
      ? this.props.shouldComponentUpdate(this.getProps(), nextProps, nextState)
      : true
  }

  render() {
    const Node = 'div'
    const children = this.props.children

    // Custom rendering function
    if (typeof this.props.render === 'function') {
      return this.props.render(this.getProps())
    } else if (this.props.component) {
      return React.createElement(this.props.component, this.getProps())
    }

    // Does not wrap child in a div if we don't have to.
    if (Array.isArray(children)) {
      return React.createElement(Node, null, React.Children.map(children, (child, i) => React.isValidElement(child)
        ? React.cloneElement(child, Object.assign({ key: i }, this.getProps()))
        : child
      ))
    } else if (children) {
      return React.isValidElement(children) ? React.cloneElement(children, this.getProps()) : children;
    } else {
      return React.createElement(Node, this.getProps())
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/Container.ts